/**
 * The "cap" chip on a Drop slots row the daily SMS cap cut or clamped
 * (user, 20/10/2026: "we must be able to see that these elements cannot
 * be sent because it will hit the daily"). A click says whose cap, how
 * much a day, and opens the master config's Auto-send tab to change it.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Ban, Scissors } from 'lucide-react';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';

import { useSectionSources } from '../../../master/section-sources';
import { capWords } from './cap-words';

export type CapMark = 'cut' | 'clamped';

export const CapChip: FC<{ mark: CapMark; position: number; plannedAmount?: number | null }> = ({ mark, position, plannedAmount = null }) => {
  const { masterRef, openMaster } = useSectionSources();
  const words = capWords(mark, masterRef, plannedAmount);
  const Icon = mark === 'cut' ? Ban : Scissors;
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={`Drop ${position}: ${words.title}`}
          title={words.title}
          className={cn(
            'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-1.5 py-px text-[10px] font-medium leading-4 transition-colors',
            mark === 'cut' ? 'border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/15' : 'border-warning/40 bg-warning/10 text-warning hover:bg-warning/20',
          )}
        >
          <Icon className="h-2.5 w-2.5" aria-hidden />
          cap
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content sideOffset={6} align="start" className="z-50 flex w-72 flex-col gap-2 rounded-lg border bg-popover p-3 text-xs shadow-lg animate-in fade-in-0 zoom-in-95">
          <span className="font-semibold">{words.title}</span>
          <p className="leading-snug text-muted-foreground">{words.body}</p>
          {masterRef ? (
            <Button size="sm" variant="outline" className="h-7 self-start text-xs" onClick={() => openMaster(masterRef.id, 'autosend')}>
              Edit the cap
            </Button>
          ) : null}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
